/// <amd-module name="Widgets/NewWorkItemWidget/NewWorkItemWidget" />
import { WorkItemType, WorkItemTypeColorAndIcon } from "Dashboards/Clients/Contracts/WorkItemTracking";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import * as Resources from "Widgets/NewWorkItemWidget/Resources";
import { IWorkItemTypeIconMetadataService } from "Wit/UI/Services";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { getWorkItemTypes } from "./NewWorkItemWidgetDataProvider";

export interface INewWorkItemWidgetState {
    title: string;
    workItemTypes: string[];
    workItemTypesLoaded?: boolean;
    selectedType?: string;
    recentlyCreatedWorkItemId?: number;
    recentlyCreatedWorkItemType?: string;
    recentlyCreatedWorkItemColorAndIcon?: WorkItemTypeColorAndIcon;
    dataIsStale?: boolean;
    isLoadingDialog?: boolean;
}

export interface NewWorkItemWidgetSettings {
    defaultWorkItemType: string;
}

export class NewWorkItemWidget extends VssComponent<WidgetProps, INewWorkItemWidgetState> {
    private static readonly titleField = "SYSTEM.TITLE";
    private static readonly newWorkItemLinkTimeout = 20;
    private static readonly Telemetry_Create = "CreateWorkItem";
    private static readonly Telemetry_WorkItemLink = "WorkItemEditLink";
    public static readonly componentType = "newWorkItemWidget";
    public static readonly maxTextLength = 255;
    private selection: DropdownSelection;

    constructor(props: WidgetProps) {
        super(props);
        this.state = {
            title: "",
            workItemTypes: []
        };

        this.selection = new DropdownSelection();
    }

    public componentDidMount() {
        try {
            const settings: NewWorkItemWidgetSettings = this.props.settings ? JSON.parse(this.props.settings) : undefined;
            let workItemTypes = getWorkItemTypes(this.context.pageContext);
            // Note - The data provider has normal behavior to return empty array if no results were available.
            // WIT experiences backed by that provider present available work item types or an empty set for cases such as missing perms.
            workItemTypes = workItemTypes.sort((a, b) => a.localeCompare(b, navigator.language));
            this.setState(
                {
                    workItemTypes: workItemTypes,
                    workItemTypesLoaded: true,
                    selectedType: (settings && settings.defaultWorkItemType) || (workItemTypes.length >= 0 && workItemTypes[0]) || undefined
                },
                this.props.onWidgetLoaded
            );
            WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
        } catch (error) {
            this.props.onWidgetLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: error
            });
        }
    }

    public componentDidUpdate(prevProps: WidgetProps) {
        if (this.props.settings !== prevProps.settings) {
            const settings: NewWorkItemWidgetSettings = this.props.settings ? JSON.parse(this.props.settings) : undefined;
            this.setState({
                selectedType:
                    (settings && settings.defaultWorkItemType) || (this.state.workItemTypes.length >= 0 && this.state.workItemTypes[0]) || undefined
            });
        }
    }

    public render(): JSX.Element {
        return (
            <div>
                <VisualTitle text={this.props.name} />
                <div className="new-work-item">
                    {this.renderWorkItemTitle()}
                    {this.renderWorkItemTypeDropdown()}
                    <div className="button-container">
                        {this.renderWorkItemLink()}
                        {this.renderCreateButton()}
                    </div>
                </div>
            </div>
        );
    }

    private renderWorkItemTitle(): JSX.Element {
        return (
            <TextField
                value={this.state.title}
                className="new-work-item-title-field"
                placeholder={Resources.EnterTitle}
                maxLength={NewWorkItemWidget.maxTextLength}
                ariaLabel={Resources.Title_AriaLabel}
                onChange={this.onTitleUpdate}
            />
        );
    }

    private renderWorkItemTypeDropdown(): JSX.Element {
        const selectedIndex = this.getSelectedTypeIndex();

        if (selectedIndex !== -1) {
            this.selection.select(selectedIndex);
        }

        const items = this.state.workItemTypes.map<IListBoxItem<string>>(item => {
            return { id: item, text: item };
        });

        return (
            <div className="new-work-item-type-field">
                <Dropdown
                    className="new-work-item-type-dropdown"
                    items={items}
                    ariaLabel={Resources.Type_AriaLabel}
                    onSelect={this.onSelectedWorkItemChanged}
                    selection={this.selection}
                />
            </div>
        );
    }

    private renderCreateButton(): JSX.Element {
        const create = this.state.isLoadingDialog ? (
            <Spinner size={SpinnerSize.small} className="spinner" />
        ) : (
            <Button
                text={Resources.CreateButtonText}
                disabled={!this.canCreateWorkItem()}
                className="new-work-item-widget-create-button"
                onClick={this.onCreateButtonClick}
                primary
            />
        );
        return <div className="add-new-work-item-button-container">{create}</div>;
    }

    private renderWorkItemLink(): JSX.Element | null {
        return (
            <div className="recently-created-work-item-container">
                {this.state.recentlyCreatedWorkItemId && (
                    <>
                        {this.state.recentlyCreatedWorkItemColorAndIcon && <WorkItemTypeIcon data={this.state.recentlyCreatedWorkItemColorAndIcon} />}
                        {this.state.recentlyCreatedWorkItemType && (
                            <Link className="recently-created-work-item" onClick={this.onClickWorkItem}>
                                {this.state.recentlyCreatedWorkItemType + " " + this.state.recentlyCreatedWorkItemId}
                            </Link>
                        )}
                    </>
                )}
            </div>
        );
    }

    private onSelectedWorkItemChanged = (event: React.SyntheticEvent<HTMLElement>, selectedItem: IListBoxItem<string>): void => {
        if (selectedItem) {
            const selectedType = this.state.workItemTypes.filter(x => x === selectedItem.id);
            if (selectedType.length) {
                this.setState({
                    selectedType: selectedType[0],
                    dataIsStale: false
                });
            }
        }
    };
    private onClickWorkItem = (): void => {
        const id = this.state.recentlyCreatedWorkItemId;
        if (id) {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: NewWorkItemWidget.Telemetry_WorkItemLink });
            layoutManager.renderCallout(onDismiss => {
                return (
                    <LegacyComponent
                        modules={["WorkItemTracking/SharedScripts/WorkItemDialogShim"]}
                        wrappedType="WorkItemDialog"
                        wrappedProps={{
                            workItemId: id,
                            onDismiss: onDismiss
                        }}
                    />
                );
            });
        }
    };

    private onCreateButtonClick = (): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: NewWorkItemWidget.Telemetry_Create });
        this.setState({ isLoadingDialog: true });
        layoutManager.renderCallout(onDismiss => {
            return (
                <LegacyComponent
                    modules={["WorkItemTracking/SharedScripts/WorkItemDialogShim"]}
                    wrappedType="createNewWorkItemDialog"
                    wrappedProps={{
                        workItemTypeName: this.state.selectedType,
                        onDismiss: () => {
                            this.setState({ isLoadingDialog: false }); // LegacyComponent doesnt expose when legacy scripts are done loading so we have to wait until the dialog is closed to react.
                            onDismiss();
                        },
                        options: {
                            close: (workItem: { id: number; workItemType: WorkItemType }) => {
                                this.onWorkItemCreated(workItem);
                            }
                        },
                        initialValues: {
                            [NewWorkItemWidget.titleField]: this.state.title
                        }
                    }}
                />
            );
        });
    };

    private onTitleUpdate = (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.setState({
            title: value,
            dataIsStale: false
        });
    };

    private onWorkItemCreated = async (workItem: { id: number; workItemType: WorkItemType }) => {
        const workItemTypeIconMetadataService = this.context.pageContext.getService<IWorkItemTypeIconMetadataService>(
            "work-item-type-icon-metadata-service"
        );
        const icon = await workItemTypeIconMetadataService.getWorkItemIcon(
            TfsWidgetContext.getProject(this.context.pageContext).name,
            workItem.workItemType.name
        );
        if (workItem.id > 0) {
            // New Work Item form returns an id of 0 if the user cancels Work Item creation.
            this.setState({
                recentlyCreatedWorkItemId: workItem.id,
                recentlyCreatedWorkItemType: workItem.workItemType.name,
                recentlyCreatedWorkItemColorAndIcon: icon,
                dataIsStale: true,
                title: ""
            });
        }
        this.setWorkItemLinkTimeout(workItem.id);
    };

    private setWorkItemLinkTimeout(recentlyCreatedWorkItemId: number) {
        window.setTimeout(() => {
            if (recentlyCreatedWorkItemId === this.state.recentlyCreatedWorkItemId) {
                this.setState({
                    recentlyCreatedWorkItemId: undefined,
                    recentlyCreatedWorkItemType: undefined,
                    recentlyCreatedWorkItemColorAndIcon: undefined
                });
            }
        }, NewWorkItemWidget.newWorkItemLinkTimeout * 1000);
    }

    private canCreateWorkItem(): boolean {
        return this.state.title.length > 0 && !this.state.dataIsStale && !!this.state.selectedType;
    }

    private getSelectedTypeIndex(): number {
        const { selectedType, workItemTypes } = this.state;
        // if no types loaded yet return -1 , if nothing selected , select first
        if (workItemTypes && workItemTypes.length > 0) {
            return selectedType ? workItemTypes.indexOf(selectedType) : 0;
        }
        return -1;
    }
}

VssComponent.register(NewWorkItemWidget.componentType, NewWorkItemWidget);
